import type { Character, Message } from "@/lib/api";
import { CharacterAvatar } from "@/CharacterAvatar";
import { Button } from "@/components/ui/button";
import { Edit3, RefreshCw, Trash2 } from "lucide-react";
import { parseMessageMarkup } from "@/lib/messageMarkup";

function formatTimestamp(createdAt: string | undefined): string | null {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function segmentClass(kind: "speech" | "action" | "thought"): string | undefined {
  switch (kind) {
    case "thought":
      return "italic opacity-75";
    case "action":
      return "font-medium opacity-90";
    default:
      return undefined;
  }
}

export function Bubble({
  m,
  charById,
  onRetry,
  onDelete,
  onDeleteTurn
  ,onEdit
}: {
  m: Message;
  charById: Map<number | undefined, Character>;
  onRetry?: () => void;
  onDelete?: () => void;
  onDeleteTurn?: () => void;
  onEdit?: () => void;
}) {
  if (m.role === "user") {
    const timestamp = formatTimestamp(m.created_at);
    return (
      <div className="flex justify-end">
        <div className="rounded-xl bg-primary px-3.5 py-2 text-sm text-primary-foreground max-w-[78%]">
          <div className="whitespace-pre-wrap">{m.content}</div>
          {timestamp && <div className="mt-0.5 text-right text-[10px] opacity-60">{timestamp}</div>}
          {onEdit && <Button aria-label="Edit pesan" title="Edit pesan" size="icon" variant="ghost" onClick={onEdit}><Edit3 /></Button>}
          {onDeleteTurn && <Button aria-label="Hapus turn" title="Hapus turn" size="icon" variant="ghost" onClick={onDeleteTurn}><Trash2 /></Button>}
        </div>
      </div>
    );
  }
  const c = charById.get(m.character_id ?? 0);
  if (m.is_error) {
    return (
      <div className="flex items-end gap-2.5">
        <CharacterAvatar character={c} />
        <div className="max-w-[90%] rounded-xl border border-red-400/30 bg-red-950/60 px-3.5 py-2.5 text-sm text-red-100">
          <div className="font-semibold">Provider error</div>
          <div className="mt-1 leading-relaxed">{m.content}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {onRetry && <Button size="sm" variant="secondary" onClick={onRetry}><RefreshCw />Kirim ulang</Button>}
            {onDelete && <Button size="sm" variant="ghost" onClick={onDelete}><Trash2 />Hapus</Button>}
          </div>
        </div>
      </div>
    );
  }
  const timestamp = formatTimestamp(m.created_at);
  return (
    <div className="flex items-end gap-2.5">
      <CharacterAvatar character={c} />
      <div
        className="max-w-[78%] rounded-xl px-3.5 py-2 text-sm"
        style={{ background: c?.color ?? "#333", color: "#111" }}
      >
        <div className="flex items-baseline gap-2">
          <div className="text-[11px] font-bold">{c?.name ?? "unknown"}</div>
          {timestamp && <div className="text-[10px] opacity-60">{timestamp}</div>}
        </div>
        <div className="whitespace-pre-wrap leading-relaxed">
          {parseMessageMarkup(m.content).map((segment, index) => (
            <span key={`${segment.kind}-${index}`} className={segmentClass(segment.kind)}>
              {segment.text}
            </span>
          ))}
          {m.is_draft && <span className="streaming-caret" aria-hidden="true" />}
        </div>
        {onDeleteTurn && <Button aria-label="Hapus turn" title="Hapus turn" size="icon" variant="ghost" onClick={onDeleteTurn}><Trash2 /></Button>}
      </div>
    </div>
  );
}